<template>
  <div>
    <ul
      v-if="!hideItems"
      class="item-list"
      :class="{
        'item-list--no-outside-borders': !outsideBorders
      }"
    >
      <li
        v-for="item in items"
        :key="item.orderItemId"
        class="item-list__item"
        :class="{
          'item-list__item--invalid': item.unavailablePerSchedule && order.orderState === 'Open' && !ignoreInvalidItems
        }"
      >
        <span
          v-if="!hideQuantity || item.quantity !== 1"
          class="item-list__quantity"
        >{{ item.quantity }}</span>

        <div class="item-list__content-wrapper">
          <div class="item-list__header">
            <h3 class="item-list__title">
              <div class="layout layout--horizontal layout--align-start">
                <div class="shrink">
                  {{ item.itemDescription }}
                </div>

                <div
                  v-if="item.schedulesPerDay"
                  class="auto-spacing-right"
                >
                  <tippy
                    arrow
                    theme="dark"
                  >
                    <button
                      type="button"
                      class="button button--light button--light-text button--icon button--smallest no-spacing"
                      tabindex="-1"
                    >
                      <span class="icon fa fa-info" />
                    </button>

                    <template #content>
                      <div>
                        <div class="layout layout--icon half-spacing">
                          <span class="icon far fa-clock dark" />
                          <h5 class="title title--6 dark">Available</h5>
                        </div>
                        <ul class="bullet-list no-spacing">
                          <li
                            v-for="(schedules, day) in item.schedulesPerDay"
                            :key="day"
                          >
                            <div class="layout layout--horizontal layout--align-start no-spacing-children">
                              <p class="no-shrink">
                                {{ day.slice(0, 3) }}:
                              </p>

                              <p class="comma-list comma-list--pipe auto-spacing-left dark right">
                                <span
                                  v-for="schedule in schedules"
                                  :key="schedule.display"
                                  class="comma-list__item"
                                >
                                  {{ schedule.display }}
                                </span>
                              </p>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </template>
                  </tippy>
                </div>
              </div>
            </h3>

            <p
              v-show="!hidePrices"
              class="item-list__price"
            >
              {{ $dollars(item.priceTotal) }}
            </p>
          </div>

          <div class="item-list__description">
            <order-item-description-text :item="item" />

            <favorite-button
              v-if="!disableFavoriting"
              :is-receipt="readOnly"
              :item="item"
              :modifiers="item.modifiers"
              :special-instructions="item.specialInstructions"
            />

            <div
              v-if="!readOnly"
              class="item-list__icon-button-group"
            >
              <button
                type="button"
                title="Edit this item"
                tabindex="-1"
                class="icon icon--link-grow far fa-pencil-alt secondary"
                @click="editItem(item)"
              />

              <button
                type="button"
                title="Remove this item"
                tabindex="-1"
                class="icon icon--link-grow far fa-trash-alt secondary"
                :class="{
                  'icon--loading': order.removingItemIds.indexOf(item.orderItemId) > -1
                }"
                @click="removeItem(item)"
              />
            </div>
          </div>
        </div>
      </li>

      <li
        v-if="order.includeUtensils"
        class="item-list__item"
      >
        <span class="item-list__quantity">*</span>
        <div class="item-list__content-wrapper">
          <div class="item-list__header">
            <h3 class="item-list__title">
              <div class="layout layout--horizontal layout--align-start">
                <div class="shrink">
                  {{ content.ReceiptStandard.metadata.utensilsIncludedMessage }}
                </div>
              </div>
            </h3>
          </div>
        </div>
      </li>

    </ul>

    <slot />

    <div
      v-if="!hideTotals && !hidePrices"
      class="key-value"
    >
      <dl class="key-value__row">
        <dt class="key-value__key">
          Subtotal
        </dt>
        <dd class="key-value__value">
          {{ $dollars(order.orderItemTotal) }}
        </dd>
      </dl>

      <template v-if="order.orderFees.length > 0">
        <dl
          v-for="{ name, amount, description } in order.orderFees"
          :key="name"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ name }}
            <button
              v-if="description"
              type="button"
              class="cursor-pointer"
              @click="$openModal('notice', { title: name, description, useSecondaryStyle: true, excludeButton: true })"
            >
              <span class="icon fal fa-info-circle gray" />
            </button>
          </dt>
          <dd class="key-value__value">
            {{ $dollars(amount) }}
          </dd>
        </dl>
      </template>

      <dl
        v-if="order.discount"
        class="key-value__row"
      >
        <dt class="key-value__key">
          Discount
        </dt>
        <dd class="key-value__value primary">
          -{{ $dollars(order.discount) }}
        </dd>
      </dl>

      <div
        v-if="order.discount && order.offers.length > 0"
        class="key-value__nested-section"
      >
        <div
          v-for="offer in order.offers"
          :key="offer.id"
          class="key-value__row key-value__row--justify-start"
        >
          <p class="key-value__key">
            {{ offer.name || offer.promotionCode }}
          </p>
          <div
            v-if="order.orderAttributes && order.orderAttributes.canRemoveOffers"
            class="key-value__value"
            @click="removeOffer(offer)"
          >
            <button
              type="button"
              title="Remove this discount"
              tabindex="-1"
              class="link danger"
              :class="{
                'link--loading': order.removingOfferIds.includes(offer.id)
              }"
            >
              Remove
            </button>
          </div>
        </div>
      </div>

      <template v-if="order.delivery || order.deliveryCharges">
        <dl class="key-value__row">
          <dt class="key-value__key">
            Delivery Charge
          </dt>
          <dd class="key-value__value">
            {{ $dollars(order.delivery ? order.delivery.fee : order.deliveryCharges) }}
          </dd>
        </dl>
      </template>

      <template v-if="order.delivery">
        <dl class="key-value__row">
          <dt
            class="key-value__key"
            @click="toggleDeliveryTaxesAndFeesOpen"
          >
            <button
              type="button"
              class="link link--icon-at-end regular"
            >
              <span>
                Tax & Fee
              </span>
              <span
                class="icon far dark-gray"
                :class="{
                  'fa-angle-right': !deliveryTaxesAndFeesOpen,
                  'fa-angle-down': deliveryTaxesAndFeesOpen
                }"
              />
            </button>
          </dt>
          <dd class="key-value__value">
            {{ $dollars(order.tax + order.serviceCharges) }}
          </dd>
        </dl>

        <div
          v-if="deliveryTaxesAndFeesOpen"
          class="key-value__nested-section"
        >
          <dl class="key-value__row">
            <dt class="key-value__key">
              Tax
            </dt>

            <dd class="key-value__value">
              {{ $dollars(order.tax) }}
            </dd>
          </dl>

          <dl
            v-if="order.serviceCharges"
            class="key-value__row"
          >
            <dt class="key-value__key">
              Service Fee
              <br>
            </dt>

            <dd class="key-value__value">
              {{ $dollars(order.serviceCharges) }}
            </dd>
          </dl>

          <div class="key-value__row">
            <p class="smallest">
              <i>The service fee helps operate the delivery provider.</i>
            </p>
          </div>
        </div>
      </template>

      <template v-else>
        <dl
          v-if="order.serviceCharges"
          class="key-value__row"
        >
          <dt class="key-value__key">
            Service Charges
          </dt>

          <dd class="key-value__value">
            {{ $dollars(order.serviceCharges) }}
          </dd>
        </dl>

        <dl
          v-if="order.tax"
          class="key-value__row"
        >
          <dt class="key-value__key">
            Tax
          </dt>
          <dd class="key-value__value">
            {{ $dollars(order.tax) }}
          </dd>
        </dl>
      </template>

      <dl
        v-if="totals.tip || order.tip"
        class="key-value__row"
      >
        <dt class="key-value__key">
          Tip
        </dt>
        <dd class="key-value__value">
          {{ $dollars(totals.tip || order.tip) }}
        </dd>
      </dl>

      <dl
        v-if="surchargeAmount"
        class="key-value__row"
      >
        <dt class="key-value__key">
          Credit Card Surcharge
        </dt>
        <dd class="key-value__value">
          {{ $dollars(surchargeAmount) }}
        </dd>
      </dl>

      <dl
        v-if="order.shipment"
        class="key-value__row"
      >
        <dt class="key-value__key">
          Shipping
        </dt>
        <dd class="key-value__value">
          {{ $dollars(order.shipment.fee) }}
        </dd>
      </dl>

      <dl class="key-value__row key-value__row--large">
        <dt class="key-value__key">
          Total
        </dt>
        <dd class="key-value__value">
          {{ $dollars(totals.totalWithTip || order.total) }}
        </dd>
      </dl>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  export default {
    name: 'OrderItems',

    props: {
      disableFavoriting: {
        type: Boolean,
        default: false
      },

      hideItems: {
        type: Boolean,
        default: false
      },

      hidePrices: {
        type: Boolean,
        default: false
      },

      hideQuantity: {
        type: Boolean,
        default: false
      },

      hideTotals: {
        type: Boolean,
        default: false
      },

      ignoreInvalidItems: {
        type: Boolean,
        default: false
      },

      order: {
        type: Object,
        required: true
      },

      orderItems: {
        type: Array,
        default: null
      },

      outsideBorders: {
        type: Boolean,
        default: false
      },

      readOnly: {
        type: Boolean,
        default: false
      },

      totals: {
        type: Object,
        default() {
          return {
            tip: 0,
            totalWithTip: 0
          };
        }
      }
    },

    data() {
      return {
        deliveryTaxesAndFeesOpen: false
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('content', ['content']),

      items() {
        if (this.orderItems) {
          return this.orderItems;
        }

        return this.order.orderItems;
      },

      surchargeAmount() {
        return this.order.payments?.find(payment => payment.surcharge)?.surcharge;
      }
    },

    methods: {
      editItem(item) {
        this.$openModal('menuItem');

        this.$store.dispatch('menu/selectItem', {
          favoriteItemId: item.favoriteItemId,
          itemId: item.menuItemId,
          orderItemId: item.orderItemId
        });
      },

      removeItem(item) {
        this.$store.dispatch('orders/removeItem', {
          orderItemId: item.orderItemId,
          orderItems: [item]
        });
      },

      async removeOffer(offer) {
        await this.$store.dispatch('orders/removeOffer', {
          id: offer.id
        });

        // this.$triggerGoogleTrackingEvent('RemoveOffer', 'Loyalty');

        this.$triggerNewRelicEvent('RemoveOffer', 'Information', 'Removed Loyalty Offer');
      },

      toggleDeliveryTaxesAndFeesOpen() {
        this.deliveryTaxesAndFeesOpen = !this.deliveryTaxesAndFeesOpen;
      }
    }
  };
</script>
